import {
  PageContainer,
  PageContent,
  PageFooterBar,
} from "../../components/PageContainer";
import _ from "lodash";
import { BreadCrumbs, TitleBar } from "../../components/TitleBar";
import { BackButton, SaveButton } from "../../components/Button";
import { useEffect, useState } from "react";
import apiAgent from "../../api/apiAgent";
import { useNavigate } from "react-router";
import { useAtomValue } from "jotai";
import { modelDataAtom, useValidateModel } from "../../atoms";
import {
  getDataAndSchemaFromModel,
  getRelationalParams,
} from "../../components/Forms/utils";
import { tMsg } from "../../components/ToastNotifications";
import { checkTitle, useOriginParams } from "../../utils";
import { useSearchParams } from "react-router-dom";
import MediaItemsForm from "./customisation/MediaItemForm";
import { useSetChangesMade } from "../../components/Publishing/utils";

const CreateMediaItem = ({
  modelName,
}: {
  modelName: "media_item" | "attract_media_item";
}): JSX.Element => {
  const [originFrom, originId] = useOriginParams();
  const modelData = useAtomValue(modelDataAtom);
  // const modelName = "media_item";
  useValidateModel(modelName);
  const setChangesMade = useSetChangesMade(originFrom || modelName);

  const capitalizedName = checkTitle(modelName);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [itemData, setItemData] = useState<any>(null);
  const [schema, setSchema] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (modelData && modelData !== null && isLoading) {
      try {
        const [emptyData, getSchema, relationships] = getDataAndSchemaFromModel(
          modelData,
          modelName,
          true
        );

        // Check to use searchParams to autofill fields
        const dataWithParams = getSchema.reduce(
          (obj, item) => {
            const key = item.name;
            const checkValue = searchParams.get(key);
            if (searchParams.get(key))
              return {
                ...obj,
                [key]:
                  item.type === "number" ? _.toNumber(checkValue) : checkValue,
              };
            return obj;
          },
          { type: "image" } // this is to set media item to image by default
        );

        const originValues = getRelationalParams(
          originFrom,
          originId,
          relationships
        );

        setItemData({ ...emptyData, ...dataWithParams, ...originValues });
        setSchema(getSchema);
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    }
  }, [modelData]);

  const saveItem = async () => {
    try {
      const getCurrent = await apiAgent.DataType.getAll(modelName);
      const removeUndefinedData = _.pickBy(itemData, (objectData) => {
        return objectData !== undefined;
      });
      // console.log("save item", itemData, removeUndefinedData);
      const response = await apiAgent.DataType.create(modelName, {
        item_rows: [
          {
            ...removeUndefinedData,
            sort_id: getCurrent?.data?.length || 1,
          },
        ],
      });

      if (response.status === 200) {
        tMsg.success(`${capitalizedName} successfully created`);
        setChangesMade();
        if (originFrom && originId)
          navigate(`/m/${originFrom}/show/${originId}`);
        else if (response?.data?.ids[0])
          navigate(`/m/${modelName}/show/${response.data.ids[0]}`);
        else navigate(`/m/${modelName}`);
      } else throw response.statusText;
    } catch (err) {
      tMsg.error(`Error creating ${capitalizedName}`);
      console.log(err);
    }
  };

  const crumbs =
    originFrom && originId
      ? [
          {
            label: `${checkTitle(originFrom)} - ${originId}`,
            link: `/m/${originFrom}/${originId}`,
          },
        ]
      : [
          {
            label: checkTitle(modelName),
            link: `/m/${modelName}`,
          },
        ];

  const customModel = `${originFrom}_media`;

  return (
    <PageContainer isLoading={isLoading} className="relative">
      <PageContent reduceHeightForFooter>
        <BreadCrumbs crumbs={crumbs} />
        <TitleBar title={`Create ${capitalizedName}`}>
          <BackButton />
        </TitleBar>
        <MediaItemsForm
          modelName={modelName}
          itemData={itemData}
          setItemData={setItemData}
          schema={schema}
          customModel={customModel}
          onSubmit={(e) => {
            e.preventDefault();
            saveItem();
          }}
        />
      </PageContent>
      <PageFooterBar>
        <SaveButton />
      </PageFooterBar>
    </PageContainer>
  );
};

export default CreateMediaItem;
